// src/components/StyledGlobalPlayer.tsx
import React, { useState, useEffect } from "react";
import { useAudio } from "../context/AudioProvider";
import { useNavigate } from "react-router-dom";
import { Menu, MenuItem } from "@mui/material";
import RepeatIcon from "@mui/icons-material/Repeat";
import RepeatOnIcon from "@mui/icons-material/RepeatOn";
import SkipPreviousIcon from "@mui/icons-material/SkipPrevious";
import PlayCircleFilledIcon from "@mui/icons-material/PlayCircleFilled";
import PauseCircleFilledIcon from "@mui/icons-material/PauseCircleFilled";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import ShuffleIcon from "@mui/icons-material/Shuffle";
import ShuffleOnIcon from "@mui/icons-material/ShuffleOn";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import AlbumIcon from "@mui/icons-material/Album";
import ShareIcon from "@mui/icons-material/Share";
import DesktopNowPlayingModal from "@/components/NowPlaying/DesktopNowPlayingModal";
import type { Artist } from '@/types';
import AddToMixtapeDialog from "@/components/mixtapes/AddToMixtapeDialog";


interface EnrichedTrack {
  id: string;
  title: string;
  preview_url: string;
  audioUrl?: string;
  duration?: number;
  albumArtwork?: string;
  albumName?: string;
  artistName?: string;
  artistProfileImage?: string;
  explicit?: boolean;
}

const formatTime = (duration?: number | string): string => {
  if (!duration) return '0:00';
  
  // If it's already in MM:SS format (string), return as-is
  if (typeof duration === 'string' && duration.includes(':')) {
    return duration;
  }
  
  // Convert to number if it's a string
  const durationNum = typeof duration === 'string' ? parseFloat(duration) : duration;
  
  // If duration is very large (> 3600), assume it's in milliseconds
  // Otherwise assume it's in seconds
  const isMilliseconds = durationNum > 3600;
  const seconds = isMilliseconds ? Math.floor(durationNum / 1000) : Math.floor(durationNum);
  
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`;
};

const API_BASE = import.meta.env.VITE_API_BASE_URL;
if (!API_BASE) throw new Error("VITE_API_BASE_URL is not defined");

const StyledGlobalPlayer: React.FC = () => {
  const { state, dispatch, togglePlay, seekTo, toggleShuffle, volume, muted, setVolume, toggleVolume } = useAudio();
  const navigate = useNavigate();
  const { currentTrack, isPlaying, progress, isLooping, isShuffling } = state;
  const enrichedTrack = currentTrack as EnrichedTrack | null;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [mixtapeDialog, setMixtapeDialog] = useState<{ open: boolean; trackId?: string }>({ open: false });
  const [viewMenuAnchor, setViewMenuAnchor] = useState<null | HTMLElement>(null);
  const currentTrackMeta = currentTrack as any;
  const playlistContextId = currentTrackMeta?.playlist_id || currentTrackMeta?.source_playlist_id;
  const playlistType = currentTrackMeta?.bundle_type || currentTrackMeta?.playlist_type || "playlist";

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const trackDuration = (() => {
    if (enrichedTrack && typeof enrichedTrack.duration === "number") {
      // If duration is very large (> 3600), assume it's in milliseconds
      // Otherwise assume it's in seconds
      const isMilliseconds = enrichedTrack.duration > 3600;
      return isMilliseconds ? Math.floor(enrichedTrack.duration / 1000) : enrichedTrack.duration;
    }
    
    // Fallback durations
    if (enrichedTrack && enrichedTrack.preview_url) {
      return 30;
    }
    
    return 180;
  })();

  const bgColor = "#181220";
  const iconColor = "#ffffff";

  const containerStyle: React.CSSProperties = {
    backgroundColor: bgColor,
    padding: "10px 30px",
    width: "100%",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  };

  const handleViewMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (playlistContextId) {
      setViewMenuAnchor(event.currentTarget);
    } else {
      handleAlbumClick();
    }
  };

  const handleViewPlaylist = () => {
    if (playlistContextId) {
      navigate(`/mixtape/${playlistContextId}`);
    }
    setViewMenuAnchor(null);
  };

  const handleViewAlbumOption = () => {
    handleAlbumClick();
    setViewMenuAnchor(null);
  };

  const leftCellStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    flexBasis: "200px",
  };

  const artworkStyle: React.CSSProperties = {
    width: "60px",
    height: "60px",
    objectFit: "cover",
    borderRadius: "5px",
    marginRight: "8px",
  };

  const trackInfoStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
  };

  const titleStyle: React.CSSProperties = {
    fontSize: "0.9rem",
    fontWeight: "bold",
    margin: 0,
    display: "flex",
    alignItems: "center",
  };

  const handleArtistClick = async () => {
    if (!enrichedTrack?.artistName) return;

    try {
      // First search for the artist by name
      const searchRes = await fetch(`${API_BASE}/public/artist?q=${encodeURIComponent(enrichedTrack.artistName)}`);
      if (!searchRes.ok) throw new Error('Failed to search for artist');
      
      const { artists } = await searchRes.json();
      if (!artists || artists.length === 0) throw new Error('Artist not found');

      // Find the exact match
      const artist = artists.find((a: Artist) => 
        a.name.toLowerCase() === enrichedTrack.artistName?.toLowerCase()
      );      
      if (!artist) throw new Error('Artist not found');

      // Get the artist by ID to get the slug
      const artistRes = await fetch(`${API_BASE}/public/artist/${artist.id}`);
      if (!artistRes.ok) throw new Error('Failed to get artist details');
      
      const artistData = await artistRes.json();
      if (!artistData.slug) throw new Error('Artist slug not found');

      // Navigate using the correct slug from the database
      navigate(`/artist/${artistData.slug}`);
    } catch (error) {
      console.error('Error navigating to artist page:', error);
      // Fallback to the URL-friendly slug if the API call fails
      const fallbackSlug = enrichedTrack.artistName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
      navigate(`/artist/${fallbackSlug}`);
    }
  };

  const handleAlbumClick = async () => {
    if (!enrichedTrack) return;

    try {
      const trackData = currentTrack as any;
      const albumId = trackData?.album_id;
      const artistId = trackData?.artist_id;

      if (!albumId) {
        console.log('No album ID available for this track');
        return;
      }

      // Try to get artist slug for hierarchical route
      if (artistId) {
        try {
          const artistRes = await fetch(`${API_BASE}/public/artist/${artistId}`);
          if (artistRes.ok) {
            const artistData = await artistRes.json();
            const artistSlug = artistData.slug || artistId;
            
            // Fetch album to get slug if available
            const albumRes = await fetch(`${API_BASE}/public/album/${albumId}`);
            if (albumRes.ok) {
              const albumData = await albumRes.json();
              const albumSlug = albumData.slug || albumId;
              navigate(`/artist/${artistSlug}/album/${albumSlug}`);
              return;
            }
          }
        } catch (error) {
          console.error('Error fetching album/artist data:', error);
        }
      }

      // Fallback: navigate to album by ID
      navigate(`/album/${albumId}`);
    } catch (error) {
      console.error('Error navigating to album:', error);
    }
  };

  const handleShareTrack = async () => {
    if (!enrichedTrack) return;

    try {
      // We need to get album and artist info for the share URL
      // The enrichedTrack should have album_id and artist_id from the original track data
      const trackData = currentTrack as any;
      const albumId = trackData?.album_id;
      const artistId = trackData?.artist_id;

      let shareUrl = window.location.origin;
      let shareText = `"${enrichedTrack.title}" by ${enrichedTrack.artistName} – Real music by real artists on Lsnto`;

      // Try to build the best possible URL
      if (albumId && artistId) {
        // Fetch artist slug
        try {
          const artistRes = await fetch(`${API_BASE}/public/artist/${artistId}`);
          if (artistRes.ok) {
            const artistData = await artistRes.json();
            const artistSlug = artistData.slug || artistId;
            
            // Fetch album to get slug if available
            const albumRes = await fetch(`${API_BASE}/public/album/${albumId}`);
            if (albumRes.ok) {
              const albumData = await albumRes.json();
              const albumSlug = albumData.slug || albumId;
              shareUrl += `/artist/${artistSlug}/album/${albumSlug}?track=${enrichedTrack.id}`;
              shareText = `"${enrichedTrack.title}" by ${enrichedTrack.artistName} from "${enrichedTrack.albumName}" – Listen on Lsnto`;
            } else {
              shareUrl += `/album/${albumId}?track=${enrichedTrack.id}`;
            }
          } else {
            shareUrl += `/album/${albumId}?track=${enrichedTrack.id}`;
          }
        } catch (error) {
          console.error('Error fetching album/artist data for share:', error);
          shareUrl += `/album/${albumId}?track=${enrichedTrack.id}`;
        }
      } else if (albumId) {
        shareUrl += `/album/${albumId}?track=${enrichedTrack.id}`;
      } else {
        // Fallback: just share the track info without a specific URL
        shareUrl = window.location.origin;
      }

      // Use native share if available
      if (navigator.share) {
        await navigator.share({
          title: `${enrichedTrack.title} - ${enrichedTrack.artistName}`,
          text: shareText,
          url: shareUrl,
        });
      } else {
        // Fallback: copy to clipboard
        await navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
        alert('Share link copied to clipboard!');
      }
    } catch (error) {
      console.error('Error sharing track:', error);
      // Silent fail for share cancellation
      if (error instanceof Error && error.name !== 'AbortError') {
        alert('Failed to share track');
      }
    }
  };

  const artistStyle: React.CSSProperties = {
    fontSize: "0.8rem",
    margin: 0,
    cursor: enrichedTrack?.artistName ? 'pointer' : 'default',
    textDecoration: enrichedTrack?.artistName ? 'underline' : 'none',
  };

  const centerCellStyle: React.CSSProperties = {
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    paddingLeft: "20px",
    paddingRight: "10px",
  };

  const controlsContainerStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    marginBottom: "6px",
  };

  const buttonStyle: React.CSSProperties = {
    background: "none",
    border: "none",
    color: iconColor,
    cursor: "pointer",
    outline: "none",
    transition: "transform 0.2s, color 0.2s",
    fontSize: "1.3rem",
  };

  const playButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    fontSize: "2.5rem",
    borderRadius: "50%",
  };

  const progressContainerStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    width: "100%",
    justifyContent: "space-between",
  };

  const progressBarStyle: React.CSSProperties = {
    flexGrow: 1,
    margin: "0 8px",
  };

  const rightCellStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    flexBasis: "100px",
    paddingRight: "50px",
  };

  const volumeControlStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    marginBottom: "6px",
    gap: "4px",
  };

  const volumeBarStyle: React.CSSProperties = {
    width: "60px",
    marginLeft: "4px",
  };

  const placeholderButtonsContainerStyle: React.CSSProperties = {
    display: "flex",
    gap: "4px",
    justifyContent: "center",
  };

  const modalStyle: React.CSSProperties = {
    position: 'fixed',
    bottom: '80px',
    left: '20px',
    width: 'calc(50vw - 200px)',
    height: 'calc(50vh - 10px)',
    backgroundColor: bgColor,
    borderRadius: '10px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
    zIndex: 1000,
    display: isModalOpen && !isMobile ? 'flex' : 'none',
    flexDirection: 'column',
    overflow: 'hidden',
  };

  const modalOverlayStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 999,
    display: isModalOpen ? 'block' : 'none',
  };

  const modalImageStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    padding: '20px',
  };

  const closeButtonStyle: React.CSSProperties = {
    position: 'absolute',
    top: '10px',
    right: '10px',
    background: 'none',
    border: 'none',
    color: iconColor,
    cursor: 'pointer',
    padding: '5px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.2s',
  };

  const handleModalClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      setIsModalOpen(false);
    }
  };

  return (
    <>
  <div style={containerStyle}>
    {/* Left */}
    <div style={leftCellStyle}>
      <img
        src={enrichedTrack?.albumArtwork || "/placeholders/album-placeholder.png"}
        alt={enrichedTrack ? enrichedTrack.title : "Album Artwork"}
        style={{ ...artworkStyle, cursor: 'pointer' }}
        onClick={() => !isMobile && setIsModalOpen(true)}
      />
      <div style={trackInfoStyle}>
        <p style={titleStyle}>
          {enrichedTrack ? enrichedTrack.title : "What do you want to Lsnto?"}
          {enrichedTrack?.explicit && (
            <span style={{ fontSize: "0.8rem", marginLeft: "8px", color: "#FF0000" }}>🅴</span>
          )}
        </p>
        <p
          style={artistStyle}
          onClick={handleArtistClick}
        >
          {enrichedTrack ? enrichedTrack.artistName : ""}
        </p>
      </div>
    </div>

        {/* Center */}
        <div style={centerCellStyle}>
          <div style={controlsContainerStyle}>
            <button style={buttonStyle} onClick={() => dispatch({ type: "TOGGLE_LOOP" })} className="control-button">
              {isLooping ? <RepeatOnIcon fontSize="inherit" /> : <RepeatIcon fontSize="inherit" />}
            </button>
            <button style={buttonStyle} onClick={() => dispatch({ type: "PREV_TRACK" })} className="control-button">
              <SkipPreviousIcon fontSize="inherit" />
            </button>
            <button style={playButtonStyle} onClick={togglePlay} className="play-button-hover control-button">
              {isPlaying ? <PauseCircleFilledIcon fontSize="inherit" /> : <PlayCircleFilledIcon fontSize="inherit" />}
            </button>
            <button style={buttonStyle} onClick={() => dispatch({ type: "NEXT_TRACK" })} className="control-button">
              <SkipNextIcon fontSize="inherit" />
            </button>
            <button style={buttonStyle} onClick={toggleShuffle} className="control-button">
              {isShuffling ? <ShuffleOnIcon fontSize="inherit" /> : <ShuffleIcon fontSize="inherit" />}
            </button>
          </div>
          <div style={progressContainerStyle}>
            <span
              style={{
                fontSize: "0.7rem",
                fontFamily: "monospace",
                width: "40px",
                textAlign: "right",
                display: "inline-block",
              }}
            >
              {formatTime(progress)}
            </span>
            <input
              type="range"
              min="0"
              max={trackDuration}
              value={progress}
              onChange={(e) => seekTo(parseFloat(e.target.value))}
              className="custom-range"
              style={{
                ...progressBarStyle,
                "--slider-value": `${(progress / trackDuration) * 100}%`,
              } as React.CSSProperties}
            />
            <span
              style={{
                fontSize: "0.7rem",
                fontFamily: "monospace",
                width: "40px",
                textAlign: "left",
                display: "inline-block",
              }}
            >
              {formatTime(trackDuration)}
            </span>
          </div>
        </div>

        {/* Right */}
        <div style={rightCellStyle}>
          <div style={volumeControlStyle}>
            <button style={buttonStyle} onClick={toggleVolume} className="control-button">
              {muted ? <VolumeOffIcon fontSize="inherit" /> : <VolumeUpIcon fontSize="inherit" />}
            </button>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={volume}
              onChange={(e) => setVolume(parseFloat(e.target.value))}
              className="volume-range"
              style={{
                ...volumeBarStyle,
                "--slider-value": `${volume * 100}%`,
              } as React.CSSProperties}
            />
          </div>
          <div style={placeholderButtonsContainerStyle}>
            <button
              style={buttonStyle}
              className="control-button"
              onClick={() => {
                if (!enrichedTrack) return;
                setMixtapeDialog({ open: true, trackId: enrichedTrack.id });
              }}
              title="Add to mixtape"
            >
              <PlaylistAddIcon fontSize="inherit" />
            </button>
            <button 
              style={buttonStyle} 
              className="control-button"
              onClick={handleViewMenuClick}
              title={playlistContextId ? "Choose destination" : "Go to album"}
            >
              <AlbumIcon fontSize="inherit" />
            </button>
            <button 
              style={buttonStyle} 
              className="control-button"
              onClick={handleShareTrack}
              title="Share this track"
            >
              <ShareIcon fontSize="inherit" />
            </button>
          </div>
        </div>
      </div>
      {isModalOpen && !isMobile && enrichedTrack && (
      <DesktopNowPlayingModal
        track={enrichedTrack}
        onClose={() => setIsModalOpen(false)}
        
      />
    )}
      <Menu
        anchorEl={viewMenuAnchor}
        open={Boolean(viewMenuAnchor)}
        onClose={() => setViewMenuAnchor(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        {playlistContextId && (
          <MenuItem onClick={handleViewPlaylist}>
            {playlistType === "user" ? "View Mixtape" : "View Playlist"}
          </MenuItem>
        )}
        <MenuItem onClick={handleViewAlbumOption}>View Album</MenuItem>
      </Menu>
      <AddToMixtapeDialog
        open={mixtapeDialog.open}
        trackId={mixtapeDialog.trackId}
        onClose={() => setMixtapeDialog({ open: false })}
      />
    
    </>
  );
};

export default React.memo(StyledGlobalPlayer);











